import React, { useEffect, useState } from 'react';
import { 
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
    Paper, Chip, Typography, Box, CircularProgress, Button, 
    IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility'; // Ver
import ContentCopyIcon from '@mui/icons-material/ContentCopy'; // Reutilizar
import SaveIcon from '@mui/icons-material/Save'; // Guardar Macro
import RefreshIcon from '@mui/icons-material/Refresh'; // Recargar
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

interface AuditLog {
    id: string;
    createdAt: string;
    externalEmail: string;
    subject?: string;
    content: string; // Añadido
    status: string;
    confirmedAt?: string;
    fileUrl?: string; // Añadido
}

const AuditLogPage = () => {
    const [logs, setLogs] = useState<AuditLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewingLog, setViewingLog] = useState<AuditLog | null>(null); // Para el modal de ver
    const navigate = useNavigate();

    const fetchLogs = async () => {
        setLoading(true);
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_URL}/api/messages/audit`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (res.ok) setLogs(await res.json());
        } catch (e) { console.error(e); } 
        finally { setLoading(false); }
    };

    useEffect(() => { fetchLogs(); }, []);

    const handleManualConfirm = async (logId: string) => {
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_URL}/api/messages/confirm-manual/${logId}`, { 
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if(res.ok) fetchLogs();
        } catch (error) { console.error(error); }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm("¿Eliminar registro?")) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_URL}/api/messages/${id}`, { 
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (res.ok) setLogs(logs.filter(log => log.id !== id));
        } catch (error) { console.error(error); }
    };

    // ✅ NUEVA ACCIÓN: REUTILIZAR (Cargar en Dashboard)
    const handleReuse = (log: AuditLog) => {
        navigate('/dashboard', { 
            state: { 
                pendingSubject: log.subject,
                pendingBody: log.content,
                pendingAttachment: log.fileUrl,
                isFromCloud: true,
                recipientEmail: log.externalEmail
            } 
        });
    };

    // ✅ NUEVA ACCIÓN: GUARDAR COMO MACRO (PLANTILLA)
    const handleSaveAsMacro = async (log: AuditLog) => {
        const macroName = window.prompt("Nombre para esta nueva plantilla (Macro):", log.subject || "Nueva Plantilla");
        if (!macroName) return;

        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_URL}/api/macros`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: macroName, content: log.content })
            });
            if (res.ok) alert("✅ Guardado como Macro exitosamente.");
        } catch (e) { alert("Error al guardar macro."); }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;

    return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2, borderTop: '4px solid #1976d2' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        Log de Auditoría de Envíos Externos
                    </Typography>
                    <Button 
                        variant="outlined" 
                        startIcon={<RefreshIcon />} 
                        onClick={fetchLogs}
                    >
                        Recargar página
                    </Button>
                </Box>
                
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #eee' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Fecha Envío</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Destinatario</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Asunto</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Confirmación</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {logs.map((log) => (
                                <TableRow key={log.id} hover>
                                    <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                                    <TableCell>{log.externalEmail}</TableCell>
                                    <TableCell>{log.subject || '(Sin Asunto)'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={log.status?.toUpperCase()} 
                                            color={log.status === 'confirmado' ? 'success' : log.status === 'visto' ? 'info' : 'warning'} 
                                            size="small" 
                                            variant={log.status === 'confirmado' ? 'filled' : 'outlined'}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {log.confirmedAt ? (
                                            <Box sx={{ color: 'success.main', display: 'flex', alignItems: 'center' }}>
                                                <CheckCircleIcon fontSize="small" sx={{ mr: 1 }} /> 
                                                {new Date(log.confirmedAt).toLocaleString()}
                                            </Box>
                                        ) : (
                                            <Button size="small" variant="outlined" onClick={() => handleManualConfirm(log.id)}>Confirmar Manual</Button>
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                            <Tooltip title="Ver contenido"><IconButton size="small" color="primary" onClick={() => setViewingLog(log)}><VisibilityIcon /></IconButton></Tooltip>
                                            <Tooltip title="Reutilizar / Reenviar"><IconButton size="small" color="secondary" onClick={() => handleReuse(log)}><ContentCopyIcon /></IconButton></Tooltip>
                                            <Tooltip title="Guardar como Macro"><IconButton size="small" sx={{ color: '#f57c00' }} onClick={() => handleSaveAsMacro(log)}><SaveIcon /></IconButton></Tooltip>
                                            <Tooltip title="Eliminar"><IconButton size="small" color="error" onClick={() => handleDelete(log.id)}><DeleteIcon /></IconButton></Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* MODAL PARA VER EL EMAIL */}
            <Dialog open={!!viewingLog} onClose={() => setViewingLog(null)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ bgcolor: '#1976d2', color: 'white' }}>Contenido del Mensaje Enviado</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="subtitle2" color="text.secondary">Asunto:</Typography>
                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>{viewingLog?.subject}</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" color="text.secondary">Mensaje:</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#f9f9f9', p: 2, borderRadius: 1 }}>{viewingLog?.content}</Typography>
                    {viewingLog?.fileUrl && (
                        <Box sx={{ mt: 2 }}>
                            <Button variant="outlined" size="small" href={viewingLog.fileUrl} target="_blank">Ver archivo enviado</Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewingLog(null)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AuditLogPage;